import { HttpClient, HttpParams } from '@angular/common/http';
import { CollectionApi, ContractNumberOrId, ContractQuery, ContractType } from './admin-contract.service';
import {
  Contract,
  ContractCluster,
  ContractInvoicingStatsWithCurrentMonth,
  ContractOrganization,
  ContractSite,
} from '../models/contract';
import { firstValueFrom, Observable } from 'rxjs';
import { addHttpParams, buildHttpParams } from '@shared/utils/request-utils';
import { Inject, Injectable } from '@angular/core';
import { getPageParams, PageRequest, SpringPage } from '@shared/page';
import { AuthService } from './auth.service';
import { Config, CONFIG } from 'config/config';
import { AbstractContractService, InvoicingFilters } from './abstract-contract.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationContractService extends AbstractContractService<ContractInvoicingStatsWithCurrentMonth> {
  constructor(@Inject(CONFIG) config: Config, http: HttpClient, auth: AuthService) {
    super(config, http, () => auth.getOrganizationEndpoint());
  }

  getAvailableContracts(page: PageRequest): Observable<SpringPage<Contract>> {
    let params: HttpParams = buildHttpParams(page);
    return this.http.get<SpringPage<Contract>>(this.url('contracts/available'), { params });
  }

  getOwnedContracts(query: ContractQuery, page: PageRequest): Observable<SpringPage<OwnedContract>> {
    let params: HttpParams = addHttpParams(query, getPageParams(page));
    return this.http.get<SpringPage<OwnedContract>>(this.url('contracts/owned'), { params });
  }

  sites(contractRef: ContractNumberOrId): CollectionUserApi<ContractSite> {
    return new CollectionUserApi<ContractSite>(this.http, this.url(`contracts/${contractRef}/sites`));
  }

  clusters(contractRef: ContractNumberOrId): CollectionUserApi<ContractCluster> {
    return new CollectionUserApi<ContractCluster>(this.http, this.url(`contracts/${contractRef}/clusters`));
  }

  organizations(contractIdOrNumber: ContractNumberOrId): CollectionApi<ContractOrganization> {
    return new CollectionApi<ContractOrganization>(
      this.http,
      this.url(`contracts/${contractIdOrNumber}/organizations`),
    );
  }
}

export class CollectionUserApi<T> {
  constructor(
    private http: HttpClient,
    private rootUri: string,
  ) {}

  getPage(page: PageRequest, request: InvoicingFilters): Promise<SpringPage<T>> {
    const params = addHttpParams(request, getPageParams(page));
    return firstValueFrom(this.http.get<SpringPage<T>>(this.rootUri, { params }));
  }
}

export interface OwnedContract {
  name: string;
  number: string;
  type: ContractType;
  startDate?: string;
  endDate?: string;
  renewalDate?: string;
  monthsaryDay?: number;

  millionRequestsPerMonth?: number;
  bandwidthPerMonth?: number;
  nbSitesAdvanced?: number;
  nbSitesExpert?: number;

  cdnEnabled: boolean;
}

export const BANDWIDTH_UNIT = 1000000000;
export const REQUESTS_UNIT = 1000000;
